import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  toggleQuestionBookmark,
  toggleQuestionLike,
} from "../services/questionBankQuestionService";

function updateQuestionInCache(currentData, questionId, updater) {
  if (!currentData?.questions?.length) {
    return currentData;
  }

  return {
    ...currentData,
    questions: currentData.questions.map((question) =>
      Number(question?.id) === Number(questionId) ? updater(question) : question
    ),
  };
}

export function useQuestionBankQuestionActions(questionQueryParams = {}) {
  const queryClient = useQueryClient();
  const queryKey = ["questionBankQuestions", questionQueryParams];

  const bookmarkMutation = useMutation({
    mutationFn: toggleQuestionBookmark,
    onMutate: async (variables) => {
      const questionId = Number(variables?.question_id);

      await queryClient.cancelQueries({ queryKey });

      const previousQuestionData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (currentData) =>
        updateQuestionInCache(currentData, questionId, (question) => ({
          ...question,
          isBookmarked: !question.isBookmarked,
        }))
      );

      return { previousQuestionData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQuestionData) {
        queryClient.setQueryData(queryKey, context.previousQuestionData);
      }
    },
    onSuccess: ({ payload }, variables) => {
      const questionId = Number(variables?.question_id);
      const nextBookmarkStatus = Number(payload?.bookmark_status ?? 0) === 1;

      queryClient.setQueriesData({ queryKey: ['questionBankQuestions'] }, (currentData) =>
        updateQuestionInCache(currentData, questionId, (question) => ({
          ...question,
          isBookmarked: nextBookmarkStatus,
        }))
      );
    },
  });

  const likeMutation = useMutation({
    mutationFn: toggleQuestionLike,
    onMutate: async (variables) => {
      const questionId = Number(variables?.question_id);

      await queryClient.cancelQueries({ queryKey });

      const previousQuestionData = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (currentData) =>
        updateQuestionInCache(currentData, questionId, (question) => {
          const nextLikedState = !question.isLiked;

          return {
            ...question,
            isLiked: nextLikedState,
            likeCount: Math.max(0, Number(question.likeCount || 0) + (nextLikedState ? 1 : -1)),
          };
        })
      );

      return { previousQuestionData };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousQuestionData) {
        queryClient.setQueryData(queryKey, context.previousQuestionData);
      }
    },
    onSuccess: ({ payload }, variables) => {
      const questionId = Number(variables?.question_id);
      const nextLikeStatus = Number(payload?.like_status ?? 0) === 1;
      const totalLiked = Number(payload?.total_liked ?? payload?.total_likes ?? 0);

      queryClient.setQueriesData({ queryKey: ['questionBankQuestions'] }, (currentData) =>
        updateQuestionInCache(currentData, questionId, (question) => ({
          ...question,
          isLiked: nextLikeStatus,
          likeCount: totalLiked,
        }))
      );
    },
  });

  return {
    toggleBookmark: bookmarkMutation,
    toggleLike: likeMutation,
  };
}
